import React, { useEffect, useContext, useState, useMemo, useRef } from "react";
import styled from "styled-components";

import { PlaylistWrapper } from "components/Playlist/PlaylistUI";
import { VideoPlayer } from "components/VideoPlayer";
import FlexContainer from "components/Gui/FlexContainer";
import { GetDocumentCover, GetDocumentFileToDownload, sortDocuments } from "utils/NwUtils";
import { ConfigContext } from "contexts/ConfigContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { light } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ARROW_LEFT_KEYCODE, ARROW_RIGHT_KEYCODE } from "../../constants";
import { BookLayoutContext } from "contexts/BookLayoutContext";
import { usePackage } from "contexts/PackageContext";
import { Popover, Tooltip } from "antd";
import { useDownload } from "hooks/useDownload";

const CoverImage = styled(FlexContainer)`
    width: ${(props) => props.Width}px !important;
    height: ${(props) => props.Height}px !important;
    background: black;
    color: white;
    font-size: 18px;
    font-weight: ${(props) => props.theme.FontWeightBold};
    text-transform: uppercase;
    z-index: 50;
    position: relative;
    svg {
        font-size: 64px;
        margin-bottom: 16px;
    }
    img {
        width: ${(props) => props.Width}px !important;
        height: ${(props) => props.Height}px !important;
        object-fit: cover;
    }
    .public-description {
        position: absolute;
        bottom: 0.25rem;
        right: 0.25rem;
        background-color: #fff;
        font-size: 0.85rem;
        padding: 0.5rem 1rem;
        color: ${(props) => props.theme.TextColor};
    }
`;

const Playlist = ({ modelFileName, playlist, isActive, modelBoxWidth }) => {
    const isCompcard = playlist && playlist.CompCard;

    const { windowHeight } = useContext(BookLayoutContext);

    const bookViewerWidth = modelBoxWidth - 60;
    const bookViewerHeight = windowHeight - 200;

    const { clientConfig } = useContext(ConfigContext);
    const { playlistTemplates, compcardTemplates, playlistViewer, exportOptions } = clientConfig;
    const { showPublicDescription } = playlistViewer;

    const { packageCode } = usePackage();
    const [page, setPage] = useState(0);

    const [isZipping, setIsZipping] = useState(false);
    const { handleZip } = useDownload(modelFileName);

    const wrapper = useRef(null);

    const isOnlyVideos = () => {
        if (playlist) {
            if (playlist.DocumentPlaylists.length > 0) {
                return playlist.DocumentPlaylists.every((document) => document.Type === "Video");
            }
        }
        return false;
    };

    const documentsPerPages = useMemo(() => {
        let documentsPerPages = [{ wide: false, documents: [] }]; //{ wide: false, documents: []}
        let index = 0,
            page = 0;
        if (playlist) {
            const documentPlaylists = sortDocuments(playlist.DocumentPlaylists);
            while (index < documentPlaylists.length) {
                if (
                    documentPlaylists[index].Type === "Video" ||
                    documentPlaylists[index].Standard.Width >= documentPlaylists[index].Standard.Height ||
                    (index === 0 && documentPlaylists[index].DocumentOrder === 1)
                ) {
                    if (index === 0) {
                        documentsPerPages[page].documents.push(documentPlaylists[index]);
                        documentsPerPages[page].wide = true;
                    } else {
                        documentsPerPages.push({ wide: true, documents: [documentPlaylists[index]] });
                        page += 1;
                    }
                } else {
                    if (documentsPerPages[page]) {
                        if (
                            documentsPerPages[page].wide ||
                            documentsPerPages[page].documents.length === 2 ||
                            (index > 0 &&
                                documentPlaylists[index].DocumentOrder > documentPlaylists[index - 1].DocumentOrder + 1)
                        ) {
                            //|| (page === 0 && documentsPerPages[page].documents.length === 1))
                            documentsPerPages.push({ wide: false, documents: [documentPlaylists[index]] });
                            page += 1;
                        } else {
                            documentsPerPages[page].documents.push(documentPlaylists[index]);
                        }
                    } else {
                        documentsPerPages.push({ wide: false, documents: [documentPlaylists[index]] });
                        page += 1;
                    }
                }
                index += 1;
            }
        }
        return documentsPerPages;
    }, [playlist]);

    useEffect(() => {
        if (isActive) {
            setPage(0);
        }
    }, [isActive]);

    useEffect(() => {
        if (isActive) {
            window.addEventListener("keydown", handleKeyDown);
            return function cleanup() {
                window.removeEventListener("keydown", handleKeyDown);
            };
        } else {
            window.removeEventListener("keydown", handleKeyDown);
        }
    }, [page, documentsPerPages, isActive]);

    useEffect(() => {
        wrapper.current.classList.add("fade");

        // Preload immagini pagine adiacenti in background
        if (documentsPerPages.length > 1) {
            const nextPage = page === documentsPerPages.length - 1 ? 0 : page + 1;
            const prevPage = page === 0 ? documentsPerPages.length - 1 : page - 1;
            preloadImages(nextPage);
            if (prevPage !== nextPage) {
                preloadImages(prevPage);
            }
        }
    }, [page]);

    const beforeChangePage = () => {
        return new Promise((resolve) => {
            wrapper.current.classList.remove("fade");
            setTimeout(resolve, 150);
        });
    };

    const preloadImages = (pageIndex) => {
        const docs = documentsPerPages[pageIndex].documents;
        return Promise.all(
            docs
                .filter(doc => doc.Type !== "Video" && doc.ID)
                .map(doc => {
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.onload = resolve;
                        img.onerror = resolve;
                        img.src = GetDocumentCover(doc);
                    });
                })
        );
    };

    const onMoveToNext = async () => {
        const nextPage = page === documentsPerPages.length - 1 ? 0 : page + 1;

        await Promise.all([
            beforeChangePage(),
            preloadImages(nextPage)
        ]);

        setPage(nextPage);
    };

    const onMoveToPrev = async () => {
        const prevPage = page === 0 ? documentsPerPages.length - 1 : page - 1;

        await Promise.all([
            beforeChangePage(),
            preloadImages(prevPage)
        ]);

        setPage(prevPage);
    };

    const handleKeyDown = (event) => {
        if (event.keyCode === ARROW_LEFT_KEYCODE) {
            onMoveToPrev();
        } else if (event.keyCode === ARROW_RIGHT_KEYCODE) {
            onMoveToNext();
        }
    };

    const printPlaylist = (template) => {
        const isCustomTemplate = template.isCustom ? "true" : "false";
        let url = "/api/packages/printplaylist?";
        url += "PackagePlaylistID=" + playlist.ID;
        url += "&PackageCode=" + packageCode;
        url += "&Template=" + template.file;
        url += "&IsCustom=" + isCustomTemplate;
        url += "&Orientation=" + template.orientation;
        window.open(url, "_blank");
    };

    const downloadPlaylistZipFromServer = () => {
        let url = "/api/packages/downloadplaylist?";
        url += "PackagePlaylistID=" + playlist.ID;
        url += "&PackageCode=" + packageCode;
        window.open(url, "_blank");
    };

    const downloadPlaylistZipFromClient = async () => {
        setIsZipping(true);
        await handleZip(getFileUrls());
        setIsZipping(false);
    };

    const downloadPlaylist = () => {
        const hasVideo = playlist.DocumentPlaylists.some((document) => document.Type === "Video");
        if (hasVideo) {
            downloadPlaylistZipFromServer();
        } else {
            downloadPlaylistZipFromClient();
        }
    };

    const getFileUrls = () => {
        let urls = [];
        playlist.DocumentPlaylists.forEach((document) => {
            const urlDownload = GetDocumentFileToDownload(document);
            if (urlDownload) {
                urls.push(urlDownload);
            }
        });
        return urls;
    };

    let boxHeight = bookViewerHeight;
    boxHeight = boxHeight > 1160 ? 1160 : boxHeight < 450 ? 450 : boxHeight;
    let boxWidth = bookViewerWidth;

    let firstImageRatio = 1;
    let firstImageWidth = 0;
    let firstImageHeight = boxHeight;
    let secondImageRatio = 1;
    let secondImageWidth = 0;
    let secondImageHeight = boxHeight;

    if (documentsPerPages[page].documents.length > 0) {
        const firstDocument = documentsPerPages[page].documents[0];
        firstImageRatio =
            !firstDocument.Standard.Height || !firstDocument.Standard.Width
                ? 1
                : firstDocument.Standard.Height / firstDocument.Standard.Width;
        firstImageWidth = Math.floor(boxHeight / firstImageRatio);
    }

    if (documentsPerPages[page].documents.length > 1) {
        let secondDocument = documentsPerPages[page].documents[1];
        secondImageRatio =
            !secondDocument.Standard.Height || !secondDocument.Standard.Width
                ? 1
                : secondDocument.Standard.Height / secondDocument.Standard.Width;
        secondImageWidth = Math.floor(boxHeight / secondImageRatio);
    }

    if (firstImageWidth + secondImageWidth > boxWidth - 4) {
        let newHeight = Math.floor((boxHeight * (boxWidth - 4)) / (firstImageWidth + secondImageWidth));
        firstImageHeight = newHeight;
        firstImageWidth = Math.floor(firstImageHeight / firstImageRatio);
        secondImageHeight = newHeight;
        secondImageWidth = Math.floor(secondImageHeight / secondImageRatio);
    }

    const renderNextPlaylistBox = (document) => {
        return (
            <CoverImage
                width={firstImageWidth}
                height={firstImageHeight}
                direction="column"
                alignItems="center"
                justifyContent="center"
            >
                <p>last page</p>
            </CoverImage>
        );
    };

    const renderDocument = (document, width, height) => {
        return (
            <CoverImage width={width} height={height} alignItems="center" justifyContent="center">
                {document.ID ? (
                    <img
                        alt="document-img"
                        id={document.ID}
                        src={`${GetDocumentCover(document)}`}
                        width={width}
                        height={height}
                    />
                ) : (
                    <FlexContainer direction="column" alignItems="center">
                        <FontAwesomeIcon icon={light("file-times")} />
                        <div>Deleted Picture</div>
                    </FlexContainer>
                )}
                {showPublicDescription && document.PublicDescription && (
                    <div className="public-description">{document.PublicDescription}</div>
                )}
            </CoverImage>
        );
    };

    return (
        <PlaylistWrapper ref={wrapper}>
            <div className="model-wrapper">
                <div className="book-container">
                    <section className="model-playlist-tools">
                        <div className="page-counter">{`${page + 1}/${documentsPerPages.length}`}</div>
                        {!isOnlyVideos() && (
                            <>
                                {isCompcard && exportOptions.pdfCompcard && (
                                    <div className="print-button">
                                        {compcardTemplates &&
                                            compcardTemplates.length > 0 &&
                                            (compcardTemplates.length === 1 ? (
                                                <Tooltip title="Print as Compcard">
                                                    <FontAwesomeIcon
                                                        onClick={() => printPlaylist(compcardTemplates[0])}
                                                        icon={light("image-polaroid-user")}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Popover
                                                    content={
                                                        <div className="template-chooser-popover">
                                                            <ul>
                                                                {compcardTemplates.map((template, index) => (
                                                                    <li
                                                                        key={template.name}
                                                                        className="print-option"
                                                                        onClick={() => printPlaylist(template)}
                                                                    >
                                                                        {template.name}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    }
                                                    placement="left"
                                                    title="Print Book"
                                                    trigger="hover"
                                                >
                                                    <FontAwesomeIcon icon={light("image-polaroid-user")} />
                                                </Popover>
                                            ))}
                                    </div>
                                )}
                                {exportOptions.pdfPlaylist && (
                                    <div className="print-button">
                                        {playlistTemplates &&
                                            playlistTemplates.length > 0 &&
                                            (playlistTemplates.length === 1 ? (
                                                <Tooltip title="Print as Portfolio">
                                                    <FontAwesomeIcon
                                                        onClick={() => printPlaylist(playlistTemplates[0])}
                                                        icon={light("file-pdf")}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Popover
                                                    content={
                                                        <div className="template-chooser-popover">
                                                            <ul>
                                                                {playlistTemplates.map((template, index) => (
                                                                    <li
                                                                        key={template.name}
                                                                        className="print-option"
                                                                        onClick={() => printPlaylist(template)}
                                                                    >
                                                                        {template.name}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    }
                                                    placement="left"
                                                    title="Print Book"
                                                    trigger="hover"
                                                >
                                                    <FontAwesomeIcon icon={light("file-pdf")} />
                                                </Popover>
                                            ))}
                                    </div>
                                )}
                            </>
                        )}
                        {exportOptions.zipDownload && (
                            <div className="download-button">
                                {isZipping ? (
                                    <FontAwesomeIcon icon={light("spinner")} spin />
                                ) : (
                                    <FontAwesomeIcon onClick={downloadPlaylist} icon={light("cloud-arrow-down")} />
                                )}
                            </div>
                        )}
                    </section>
                    <div className="book-viewer">
                        <div
                            className={`picbox ${documentsPerPages[page].documents.length > 1 ? "picbox-double" : ""}`}
                            style={{ height: boxHeight }}
                        >
                            {documentsPerPages[page].documents.length > 0 && (
                                <>
                                    {documentsPerPages[page].documents[0].Type === "next-playlist" ? (
                                        renderNextPlaylistBox(documentsPerPages[page].documents[0])
                                    ) : documentsPerPages[page].documents[0].Type === "Video" ? (
                                        <VideoPlayer
                                            //defaultPlay
                                            style={{ width: boxWidth }}
                                            document={documentsPerPages[page].documents[0]}
                                        />
                                    ) : (
                                        renderDocument(
                                            documentsPerPages[page].documents[0],
                                            firstImageWidth,
                                            firstImageHeight,
                                        )
                                    )}

                                    {documentsPerPages[page].documents.length > 1 && (
                                        <>
                                            {documentsPerPages[page].documents[1].Type === "next-playlist"
                                                ? renderNextPlaylistBox(documentsPerPages[page].documents[1])
                                                : renderDocument(
                                                      documentsPerPages[page].documents[1],
                                                      secondImageWidth,
                                                      secondImageHeight,
                                                  )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        {documentsPerPages.length > 1 ? (
                            <>
                                <div className="book-prev" onClick={onMoveToPrev}>
                                    <FontAwesomeIcon icon={light("chevron-left")} style={{ cursor: "pointer" }} />
                                </div>
                                <div className="book-next" onClick={onMoveToNext}>
                                    <FontAwesomeIcon icon={light("chevron-right")} style={{ cursor: "pointer" }} />
                                </div>
                                {page !== 0 &&
                                    documentsPerPages[page].documents.length &&
                                    documentsPerPages[page].documents[0].Type !== "Video" && (
                                        <div className="hover-hotspot hover-left" onClick={onMoveToPrev}></div>
                                    )}
                                {documentsPerPages[page].documents.length !== 0 &&
                                    documentsPerPages[page].documents[0].Type !== "Video" && (
                                        <div className="hover-hotspot hover-right" onClick={onMoveToNext}></div>
                                    )}
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </PlaylistWrapper>
    );
};

export default Playlist;
